import { css, html, LitElement } from 'lit';
import { FetchMixin } from '../FetchMixin';

class DashboardLoginContainer extends FetchMixin(LitElement) {
    static styles = css`
        :host {
            user-select: none;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: calc(100vh - 60px);
            background-color: #f3f4f6;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }

        /* ログインフォームをカード状にする */
        .login-card {
            background-color: #ffffff;
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 4px 10px rgba(0, 0, 0, 0.03);
            padding: 48px 40px;
            max-width: 400px;
            width: 100%;
            box-sizing: border-box;
            text-align: center;
        }

        /* サービスのロゴ用スペース */
        .logo-placeholder {
            width: 64px;
            height: 64px;
            background-color: #e5e7eb;
            border-radius: 16px;
            margin: 0 auto 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: #9ca3af;
        }

        /* タイトルとサブタイトルでメリハリをつける */
        .title {
            font-size: 24px;
            font-weight: bold;
            color: #111827;
            margin: 0 0 8px 0;
        }

        .subtitle {
            font-size: 14px;
            color: #6b7280;
            margin: 0 0 32px 0;
        }

        .button-container {
            display: flex;
            justify-content: center;
            margin-bottom: 32px;
        }

        /* 規約テキストを控えめにし、洗練された印象に */
        .terms-container {
            font-size: 12px;
            color: #9ca3af;
            line-height: 1.6;
        }

        .terms-container a {
            color: #4f46e5; /* リンクは控えめなブルー系など */
            text-decoration: none;
            font-weight: 500;
        }

        .terms-container a:hover {
            text-decoration: underline;
        }
    `;

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.loadAdmax();

        window.addEventListener('message', this.handleAdmaxMessage.bind(this));
        window.addEventListener('focus', this.loadAdmax.bind(this));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('message', this.handleAdmaxMessage.bind(this));
        window.removeEventListener('focus', this.loadAdmax.bind(this));
    }

    handleAdmaxMessage(event) {
        if (event.data.type === 'admaxSize') {
            const { width, height } = event.data;
            const admaxIframe = this.shadowRoot.getElementById('admax-iframe');
            if (admaxIframe) {
                admaxIframe.style.width = `${width}px`;
                admaxIframe.style.height = `${height}px`;
            }
        }
    }

    loadAdmax() {
        const admaxId = process.env.ADMAX_ID;
        requestAnimationFrame(() => {
            const admaxIframe = this.shadowRoot.getElementById('admax-iframe');
            if (admaxId && admaxIframe) {
                admaxIframe.src = `admax-iframe.html?admaxId=${encodeURIComponent(admaxId)}`;
            }
        });
    }

    firstUpdated() {
        if (window.google && google.accounts) {
            this.initializeGoogleSignIn();
        } else {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => this.initializeGoogleSignIn();
            document.head.appendChild(script);
        }
    }

    initializeGoogleSignIn() {
        google.accounts.id.initialize({
            client_id: process.env.LOGIN_CLIENT_ID,
            callback: this.handleCredentialResponse.bind(this),
            auto_select: true,          // 既知のユーザーの自動ログインを試行
            use_fedcm_for_prompt: true, // ブラウザ統合UI（FedCM）を有効化
            cancel_on_tap_outside: false,
        });

        // ボタンのデザインをカードに合わせる
        google.accounts.id.renderButton(
            this.shadowRoot.querySelector("#signin-button"),
            {
                theme: "outline",     // 白背景の標準的なGoogleボタン
                size: "large",
                width: 320,           // カード幅に合わせたサイズ
                shape: "pill",        // よりモダンな丸みのある形状
                logo_alignment: "center"
            }
        );

        // One Tapプロンプト（画面右上のログイン提案）を表示
        google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
                console.log("One Tap not displayed:", notification.getNotDisplayedReason());
            }
        });
    }

    handleCredentialResponse(response) {
        const id_token = response.credential;
        const data = { id_token: id_token };

        const options = {
            method: 'POST',
            body: JSON.stringify(data)
        }

        const endpoint = process.env.LOGIN_ENDPOINT;
        this.fetchWithNonAuth(endpoint, options)
            .then(() => {
                location.reload();
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    }

    render() {
        const termsUrl = `${process.env.BRAND_URL_BASE}terms.html`;
        const privacyUrl = `${process.env.BRAND_URL_BASE}privacy-policy.html`;

        return html`
            <div class="login-card">
                <!-- サービスロゴのプレースホルダー -->
                <div class="logo-placeholder"><img src="misc/logo.png" width="64" height="64" alt="Logo" /></div>
                
                <h1 class="title">ログイン</h1>
                <p class="subtitle">Googleアカウントでサービスを続行</p>

                <div class="button-container">
                    <!-- HTML側の初期化タグはJS側と競合するため削除し、ボタン受け皿のみ保持 -->
                    <div id="signin-button"></div>
                </div>

                <div class="terms-container">
                    サービスをご利用の前に、<a href="${termsUrl}" target="_blank">利用規約</a>と<a href="${privacyUrl}" target="_blank">プライバシーポリシー</a>をご確認ください。<br>
                    ログインすると、上記に同意したことになります。
                </div>

                <!-- JSから参照しているiframeを追加（広告用などに使用） -->
                <iframe id="admax-iframe" style="display:none; border:none;"></iframe>
            </div>
        `;
    }
}

customElements.define('dashboard-login-container', DashboardLoginContainer);